import React, { useState } from 'react';
import { uploadMenuItemsToFirebase, getMenuItemsFromFirebase, getCategoriesFromFirebase } from '../utils/firebaseUpload';
import { uploadAllFoodImages } from '../utils/firebaseImageUpload';
import { fetchMenuItems, fetchCategories } from '../services/menuService';
import { ref, push } from 'firebase/database';
import { realtimeDb } from '../firebase/config';

const AdminPage = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isCreatingTestOrder, setIsCreatingTestOrder] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [imageUploadStatus, setImageUploadStatus] = useState('');
  const [testOrderStatus, setTestOrderStatus] = useState('');
  const [firebaseItems, setFirebaseItems] = useState([]);
  const [firebaseCategories, setFirebaseCategories] = useState([]);
  const [dataSource, setDataSource] = useState('Unknown');

  const createTestOrder = async () => {
    setIsCreatingTestOrder(true);
    setTestOrderStatus('Creating test order...');
    
    try {
      // Check if Firebase Realtime Database is configured
      if (!process.env.REACT_APP_FIREBASE_PROJECT_ID) {
        throw new Error('Firebase project ID not configured');
      }

      console.log('Creating test order...');
      console.log('Database URL:', process.env.REACT_APP_FIREBASE_DATABASE_URL || 
        `https://${process.env.REACT_APP_FIREBASE_PROJECT_ID}-default-rtdb.asia-southeast1.firebasedatabase.app`);

      const testOrder = {
        customerInfo: {
          name: 'Test Customer',
          phone: '0412 345 678',
          email: 'test@example.com',
          address: '123 Test Street, Willetton, WA 6155'
        },
        items: [
          {
            id: 'kottu',
            name: 'Kottu',
            price: 25.00,
            quantity: 2,
            variation: 'Chicken'
          },
          {
            id: 'biriyani',
            name: 'Biriyani',
            price: 24.00,
            quantity: 1,
            variation: 'Chicken'
          },
          {
            id: 'butter-chicken',
            name: 'Butter Chicken',
            price: 25.00,
            quantity: 1
          }
        ],
        status: 'pending',
        timestamp: Date.now(),
        updatedAt: Date.now(),
        specialInstructions: 'Please make it extra spicy and deliver to the back door',
        subtotal: 99.00,
        serviceFee: 4.95,
        total: 103.95,
        statusHistory: [
          {
            status: 'pending',
            timestamp: Date.now()
          }
        ]
      };

      console.log('Test order data:', testOrder);

      const ordersRef = ref(realtimeDb, 'orders');
      console.log('Orders reference created');
      
      const newOrderRef = await push(ordersRef, testOrder);
      console.log('Order pushed successfully, key:', newOrderRef.key);
      
      setTestOrderStatus(`✅ Test order created successfully! Order ID: ${newOrderRef.key}`);
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setTestOrderStatus('');
      }, 5000);
      
    } catch (error) {
      console.error('Error creating test order:', error);
      setTestOrderStatus(`❌ Error creating test order: ${error.message}`);
      
      // Provide more specific error messages
      if (error.message.includes('permission')) {
        setTestOrderStatus('❌ Permission denied. Check Firebase Realtime Database rules.');
      } else if (error.message.includes('network')) {
        setTestOrderStatus('❌ Network error. Check your internet connection.');
      } else if (error.message.includes('region')) {
        setTestOrderStatus('❌ Database region error. Check Firebase configuration.');
      }
    } finally {
      setIsCreatingTestOrder(false);
    }
  };

  const handleUploadToFirebase = async () => {
    setIsUploading(true);
    setUploadStatus('Uploading menu items to Firebase...');
    
    try {
      const result = await uploadMenuItemsToFirebase();
      
      if (result.success) {
        setUploadStatus('✅ Menu items uploaded successfully to Firebase!');
        // Refresh the data
        await loadFirebaseData();
      } else {
        setUploadStatus(`❌ Upload failed: ${result.error}`);
      }
    } catch (error) {
      setUploadStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadImages = async () => {
    setIsUploadingImages(true);
    setImageUploadStatus('Uploading food images to Firebase Storage...');
    
    try {
      const result = await uploadAllFoodImages();
      
      if (result.success) {
        setImageUploadStatus(`✅ ${result.message}!`);
        // Refresh the data to show updated image URLs
        await loadFirebaseData();
      } else {
        setImageUploadStatus(`❌ Image upload failed: ${result.error}`);
      }
    } catch (error) {
      setImageUploadStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsUploadingImages(false);
    }
  };

  const loadFirebaseData = async () => {
    try {
      const [items, categories] = await Promise.all([
        getMenuItemsFromFirebase(),
        getCategoriesFromFirebase()
      ]);
      setFirebaseItems(items);
      setFirebaseCategories(categories);
      setDataSource('Firebase');
    } catch (error) {
      console.error('Error loading Firebase data:', error);
      setDataSource('Firebase Error');
    }
  };

  const checkDataSource = async () => {
    try {
      // Try to fetch from Firebase
      const [items, categories] = await Promise.all([
        fetchMenuItems(),
        fetchCategories()
      ]);
      
      // Check if we got data from Firebase
      if (items.length > 0 && categories.length > 0) {
        // Check if any item has Firebase-specific fields
        const hasFirebaseFields = items.some(item => item.createdAt && item.updatedAt);
        setDataSource(hasFirebaseFields ? 'Firebase' : 'Firebase (No Timestamps)');
      } else {
        setDataSource('Firebase (No Data)');
      }
    } catch (error) {
      console.error('Error checking data source:', error);
      setDataSource('Firebase Error');
    }
  };

  React.useEffect(() => {
    loadFirebaseData();
    checkDataSource();
  }, []);

  return (
    <div className="admin-page">
      <div className="section">
        <div className="section-title">
          <h1>Admin Panel</h1>
          <p>Manage menu items and Firebase data</p>
        </div>

        {/* Data Source Status */}
        <div className="card p-4 mb-4">
          <h3>Data Source Status</h3>
          <div className="d-flex justify-between align-center">
            <div>
              <p><strong>Current Data Source:</strong> {dataSource}</p>
              <p><strong>Firebase Configured:</strong> {process.env.REACT_APP_FIREBASE_API_KEY ? '✅ Yes' : '❌ No'}</p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={checkDataSource}
            >
              Refresh Status
            </button>
          </div>
        </div>

        <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {/* Upload Section */}
          <div className="card p-4">
            <h3>Upload Menu to Firebase</h3>
            <p>Upload all menu items from the local data to Firebase Firestore.</p>
            
            <div className="mb-3">
              <strong>Note:</strong> The app now only uses Firebase data. Local data is only used for initial upload.
            </div>
            
            <button
              className="btn btn-primary w-full"
              onClick={handleUploadToFirebase}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload to Firebase'}
            </button>
            
            {uploadStatus && (
              <div className="mt-3 p-3" style={{ 
                backgroundColor: uploadStatus.includes('✅') ? '#d4edda' : '#f8d7da',
                border: `1px solid ${uploadStatus.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '4px',
                color: uploadStatus.includes('✅') ? '#155724' : '#721c24'
              }}>
                {uploadStatus}
              </div>
            )}
          </div>

          {/* Image Upload Section */}
          <div className="card p-4">
            <h3>Upload Food Images</h3>
            <p>Upload food images to Firebase Storage and link them to menu items.</p>
            
            <div className="mb-3">
              <strong>Available Images:</strong> 8 food images
            </div>
            
            <div className="mb-3">
              <strong>Images to Upload:</strong>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1rem', fontSize: '0.9rem' }}>
                <li>Kottu (chickenkottu-506x405.png)</li>
                <li>Nasi Goreng (nasigoreng-506x405.png)</li>
                <li>Rice & Curry (riceandcurryfish-506x405.png)</li>
                <li>Biriyani (chickenbiryani-506x405.png)</li>
                <li>Fried Rice (chillichickenfriedrice-506x405.png)</li>
                <li>Noodles (noodles-506x405.png)</li>
                <li>Lamprais (lamprais-506x405.png)</li>
                <li>String Hopper Pilau (stringhopperspilau-506x405.png)</li>
              </ul>
            </div>
            
            <button
              className="btn btn-primary w-full"
              onClick={handleUploadImages}
              disabled={isUploadingImages}
            >
              {isUploadingImages ? 'Uploading Images...' : 'Upload Images to Firebase'}
            </button>
            
            {imageUploadStatus && (
              <div className="mt-3 p-3" style={{ 
                backgroundColor: imageUploadStatus.includes('✅') ? '#d4edda' : '#f8d7da',
                border: `1px solid ${imageUploadStatus.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '4px',
                color: imageUploadStatus.includes('✅') ? '#155724' : '#721c24'
              }}>
                {imageUploadStatus}
              </div>
            )}
          </div>
        </div>

        {/* Test Order Section */}
        <div className="card p-4 mt-4">
          <h3>Test Restaurant Dashboard</h3>
          <p>Create a fake order to test the restaurant dashboard functionality.</p>
          
          <div className="mb-3">
            <strong>Test Order Details:</strong>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1rem', fontSize: '0.9rem' }}>
              <li>Customer: Test Customer (0412 345 678)</li>
              <li>Items: 2x Kottu, 1x Biriyani, 1x Butter Chicken</li>
              <li>Total: $103.95 (including service fee)</li>
              <li>Status: Pending</li>
              <li>Special Instructions: "Please make it extra spicy and deliver to the back door"</li>
            </ul>
          </div>
          
          <button
            className="btn btn-primary w-full"
            onClick={createTestOrder}
            disabled={isCreatingTestOrder}
          >
            {isCreatingTestOrder ? 'Creating Test Order...' : 'Create Test Order'}
          </button>
          
          {testOrderStatus && (
            <div className="mt-3 p-3" style={{ 
              backgroundColor: testOrderStatus.includes('✅') ? '#d4edda' : '#f8d7da',
              border: `1px solid ${testOrderStatus.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '4px',
              color: testOrderStatus.includes('✅') ? '#155724' : '#721c24'
            }}>
              {testOrderStatus}
            </div>
          )}
          
          <div className="mt-3">
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              <strong>Next Steps:</strong>
            </p>
            <ol style={{ fontSize: '0.9rem', color: '#666', marginLeft: '1rem' }}>
              <li>Click "Create Test Order" above</li>
              <li>Navigate to <code>/restaurant</code> to view the restaurant dashboard</li>
              <li>Test order management features like status updates</li>
              <li>Test customer calling and map viewing functionality</li>
            </ol>
          </div>
        </div>

        {/* Firebase Data Section */}
        <div className="card p-4 mt-4">
          <h3>Firebase Data</h3>
          <p>Current data stored in Firebase Firestore.</p>
          
          <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <div className="mb-3">
                <strong>Firebase Menu Items:</strong> {firebaseItems.length} items
              </div>
              
              <div className="mb-3">
                <strong>Firebase Categories:</strong> {firebaseCategories.length} categories
              </div>
              
              <button
                className="btn btn-secondary w-full"
                onClick={loadFirebaseData}
              >
                Refresh Data
              </button>
            </div>

            <div>
              <h4>Images with Firebase URLs:</h4>
              <div style={{ fontSize: '0.9rem' }}>
                {firebaseItems
                  .filter(item => item.image && item.image.startsWith('https://'))
                  .map(item => (
                    <div key={item.id} style={{ marginBottom: '0.5rem' }}>
                      ✅ {item.name}
                    </div>
                  ))}
              </div>
              
              <h4 style={{ marginTop: '1rem' }}>Images with Local URLs:</h4>
              <div style={{ fontSize: '0.9rem' }}>
                {firebaseItems
                  .filter(item => item.image && item.image.startsWith('/images/'))
                  .map(item => (
                    <div key={item.id} style={{ marginBottom: '0.5rem' }}>
                      📁 {item.name}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Firebase Items List */}
        {firebaseItems.length > 0 && (
          <div className="card p-4 mt-4">
            <h3>Firebase Menu Items</h3>
            <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {firebaseItems.map((item) => (
                <div key={item.id} className="card p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <h4>{item.name}</h4>
                  <p><strong>Category:</strong> {item.category}</p>
                  <p><strong>Price:</strong> ${item.price}</p>
                  <p><strong>Dietary:</strong> {item.dietary?.join(', ') || 'None'}</p>
                  <p><strong>Image:</strong> {item.image ? '✅' : '❌'}</p>
                  {item.image && (
                    <p style={{ fontSize: '0.8rem', color: '#666', wordBreak: 'break-all' }}>
                      {item.image.startsWith('https://') ? 'Firebase Storage' : 'Local Path'}
                    </p>
                  )}
                  {item.variations?.length > 0 && (
                    <p><strong>Variations:</strong> {item.variations.length}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Firebase Categories List */}
        {firebaseCategories.length > 0 && (
          <div className="card p-4 mt-4">
            <h3>Firebase Categories</h3>
            <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
              {firebaseCategories.map((category) => (
                <div key={category.id} className="card p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <h4>{category.name}</h4>
                  <p><strong>ID:</strong> {category.id}</p>
                  <p>{category.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage; 